import { lazy } from 'react';

// material-ui
import { Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';
import { FormattedMessage } from 'react-intl';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';

// lazy loaded components
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));
const EntityNameSelect = lazy(() => import('ui-component/fields/EntityNameSelect'));
const FieldLogTypeOperationSelect = lazy(() => import('ui-component/fields/LogTypeOperationSelect'));
const OperationEventCodeSelect = lazy(() => import('ui-component/fields/OperationEventCodeSelect'));
const OperationStatusSelect = lazy(() => import('ui-component/fields/OperationStatusSelect'));
const OperationContextSelect = lazy(() => import('ui-component/fields/OperationContextSelect'));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => [
                ...appGridActions(capabilities, params?.row, permissionData, onClickActions)
            ]
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'entityName',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-entityname'
        },
        {
            field: 'logType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-logtype'
        },
        {
            field: 'eventCode',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={<FormattedMessage id={`operationeventcode-${String(params.value).toLowerCase()}`} />}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FormattedMessage id={`operationeventcode-${String(params.value).toLowerCase()}`} />
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-log-operation-eventcode'
        },
        {
            field: 'status',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={<FormattedMessage id={`ui-comp-form-operationstatus-${String(params.value).toLowerCase()}`} />}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FormattedMessage id={`ui-comp-form-operationstatus-${String(params.value).toLowerCase()}`} />
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-log-operation-status'
        },
        {
            field: 'context',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={<FormattedMessage id={`ui-comp-form-operationcontext-${String(params.value).toLowerCase()}`} />}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FormattedMessage id={`ui-comp-form-operationcontext-${String(params.value).toLowerCase()}`} />
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-log-operation-context'
        },
        {
            field: 'entityClass',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-entityclass'
        },
        {
            field: 'entityId',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-entityid'
        },
        {
            field: 'entityCode',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-entitycode'
        },
        {
            field: 'entityLabel',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-entitylabel'
        },
        {
            field: 'summary',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            flex: 1,
            renderCell: (params) =>
                params?.value && (
                    // eslint-disable-next-line react/no-danger
                    <Tooltip title={<div dangerouslySetInnerHTML={{ __html: params.value }} />}>
                        {/* eslint-disable-next-line react/no-danger */}
                        <div dangerouslySetInnerHTML={{ __html: params.value }} />
                    </Tooltip>
                ),
            label: 'traits-appentityisloggable-summary'
        },
        {
            field: 'data',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={JSON.stringify(params.value)}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {JSON.stringify(params.value)}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityisloggable-data'
        },
        {
            field: 'parentEntityClass',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-parententityclass'
        },
        {
            field: 'parentEntityId',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-parententityid'
        },
        {
            field: 'parentEntityCode',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-parententitycode'
        },
        {
            field: 'rootEntityClass',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-rootentityclass'
        },
        {
            field: 'rootEntityId',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-rootentityid'
        },
        {
            field: 'rootEntityCode',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-rootentitycode'
        },
        {
            field: 'sourceEntityClass',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-sourceentityclass'
        },
        {
            field: 'sourceEntityId',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-sourceentityid'
        },
        {
            field: 'sourceEntityCode',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-sourceentitycode'
        },
        {
            field: 'routeName',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-routename'
        },
        {
            field: 'requestMethod',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-requestmethod'
        },
        {
            field: 'requestPath',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-requestpath'
        },
        {
            field: 'commandName',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-commandname'
        },
        {
            field: 'connectionName',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-log-operation-connectionname'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                entityName: true,
                logType: true,
                eventCode: true,
                status: true,
                context: true,
                entityClass: false,
                entityId: false,
                entityCode: true,
                entityLabel: true,
                summary: true,
                data: false,
                parentEntityClass: false,
                parentEntityId: false,
                parentEntityCode: false,
                rootEntityClass: false,
                rootEntityId: false,
                rootEntityCode: false,
                sourceEntityClass: false,
                sourceEntityId: false,
                sourceEntityCode: false,
                routeName: false,
                requestMethod: false,
                requestPath: false,
                commandName: false,
                connectionName: false,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'entityName',
                'logType',
                'eventCode',
                'status',
                'context',
                'entityClass',
                'entityId',
                'entityCode',
                'entityLabel',
                'summary',
                'data',
                'parentEntityClass',
                'parentEntityId',
                'parentEntityCode',
                'rootEntityClass',
                'rootEntityId',
                'rootEntityCode',
                'sourceEntityClass',
                'sourceEntityId',
                'sourceEntityCode',
                'routeName',
                'requestMethod',
                'requestPath',
                'commandName',
                'connectionName',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 90 },
                entityName: { width: 160 },
                logType: { width: 130 },
                eventCode: { width: 240 },
                status: { width: 110 },
                context: { width: 110 },
                entityClass: { width: 220 },
                entityId: { width: 110 },
                entityCode: { width: 140 },
                entityLabel: { width: 220 },
                summary: { width: 220 },
                data: { width: 180 },
                parentEntityClass: { width: 220 },
                parentEntityId: { width: 110 },
                parentEntityCode: { width: 140 },
                rootEntityClass: { width: 220 },
                rootEntityId: { width: 110 },
                rootEntityCode: { width: 140 },
                sourceEntityClass: { width: 220 },
                sourceEntityId: { width: 110 },
                sourceEntityCode: { width: 140 },
                routeName: { width: 180 },
                requestMethod: { width: 120 },
                requestPath: { width: 260 },
                commandName: { width: 180 },
                connectionName: { width: 180 },
                createdAt: { width: 150 },
                createdBy: { width: 160 },
                updatedAt: { width: 150 },
                updatedBy: { width: 160 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
        }
    },
    filterDef: [
        {
            filterType: 'array',
            field: { fieldName: 'entityName', fieldType: 'simple' },
            label: 'entity-log-operation-entityname',
            renderField: (params) => <EntityNameSelect key="filterLogOper-entityName" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'logType', fieldType: 'simple' },
            label: 'entity-log-operation-logtype',
            renderField: (params) => <FieldLogTypeOperationSelect key="filterLogOper-logType" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'eventCode', fieldType: 'simple' },
            label: 'entity-log-operation-eventcode',
            renderField: (params) => <OperationEventCodeSelect key="filterLogOper-eventCode" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'status', fieldType: 'simple' },
            label: 'entity-log-operation-status',
            renderField: (params) => <OperationStatusSelect key="filterLogOper-status" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'context', fieldType: 'simple' },
            label: 'entity-log-operation-context',
            renderField: (params) => <OperationContextSelect key="filterLogOper-context" multiple {...params} />
        },
        {
            filterType: 'string',
            field: { fieldName: 'entityClass', fieldType: 'simple' },
            label: 'entity-log-operation-entityclass'
        },
        {
            filterType: 'string',
            field: { fieldName: 'entityId', fieldType: 'simple' },
            label: 'entity-log-operation-entityid'
        },
        {
            filterType: 'string',
            field: { fieldName: 'entityCode', fieldType: 'simple' },
            label: 'entity-log-operation-entitycode'
        },
        {
            filterType: 'string',
            field: { fieldName: 'entityLabel', fieldType: 'simple' },
            label: 'entity-log-operation-entitylabel'
        },
        {
            filterType: 'string',
            field: { fieldName: 'summary', fieldType: 'simple' },
            label: 'traits-appentityisloggable-summary'
        },
        {
            filterType: 'string',
            field: { fieldName: 'parentEntityClass', fieldType: 'simple' },
            label: 'entity-log-operation-parententityclass'
        },
        {
            filterType: 'string',
            field: { fieldName: 'parentEntityId', fieldType: 'simple' },
            label: 'entity-log-operation-parententityid'
        },
        {
            filterType: 'string',
            field: { fieldName: 'parentEntityCode', fieldType: 'simple' },
            label: 'entity-log-operation-parententitycode'
        },
        {
            filterType: 'string',
            field: { fieldName: 'rootEntityClass', fieldType: 'simple' },
            label: 'entity-log-operation-rootentityclass'
        },
        {
            filterType: 'string',
            field: { fieldName: 'rootEntityId', fieldType: 'simple' },
            label: 'entity-log-operation-rootentityid'
        },
        {
            filterType: 'string',
            field: { fieldName: 'rootEntityCode', fieldType: 'simple' },
            label: 'entity-log-operation-rootentitycode'
        },
        {
            filterType: 'string',
            field: { fieldName: 'sourceEntityClass', fieldType: 'simple' },
            label: 'entity-log-operation-sourceentityclass'
        },
        {
            filterType: 'string',
            field: { fieldName: 'sourceEntityId', fieldType: 'simple' },
            label: 'entity-log-operation-sourceentityid'
        },
        {
            filterType: 'string',
            field: { fieldName: 'sourceEntityCode', fieldType: 'simple' },
            label: 'entity-log-operation-sourceentitycode'
        },
        {
            filterType: 'string',
            field: { fieldName: 'routeName', fieldType: 'simple' },
            label: 'entity-log-operation-routename'
        },
        {
            filterType: 'string',
            field: { fieldName: 'requestMethod', fieldType: 'simple' },
            label: 'entity-log-operation-requestmethod'
        },
        {
            filterType: 'string',
            field: { fieldName: 'requestPath', fieldType: 'simple' },
            label: 'entity-log-operation-requestpath'
        },
        {
            filterType: 'string',
            field: { fieldName: 'commandName', fieldType: 'simple' },
            label: 'entity-log-operation-commandname'
        },
        {
            filterType: 'string',
            field: { fieldName: 'connectionName', fieldType: 'simple' },
            label: 'entity-log-operation-connectionname'
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterLogOper-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterLogOper-updatedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-logoperation',
        fields: [
            { fieldName: 'entityName', fieldType: 'simple' },
            { fieldName: 'logType', fieldType: 'simple' },
            { fieldName: 'eventCode', fieldType: 'simple' },
            { fieldName: 'entityCode', fieldType: 'simple' },
            { fieldName: 'entityLabel', fieldType: 'simple' },
            { fieldName: 'parentEntityCode', fieldType: 'simple' },
            { fieldName: 'rootEntityCode', fieldType: 'simple' },
            { fieldName: 'sourceEntityCode', fieldType: 'simple' },
            { fieldName: 'routeName', fieldType: 'simple' },
            { fieldName: 'requestPath', fieldType: 'simple' },
            { fieldName: 'commandName', fieldType: 'simple' },
            { fieldName: 'summary', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'entityName', fieldType: 'simple' },
        { fieldName: 'logType', fieldType: 'simple' },
        { fieldName: 'eventCode', fieldType: 'simple' },
        { fieldName: 'status', fieldType: 'simple' },
        { fieldName: 'context', fieldType: 'simple' },
        { fieldName: 'entityClass', fieldType: 'simple' },
        { fieldName: 'entityId', fieldType: 'simple' },
        { fieldName: 'entityCode', fieldType: 'simple' },
        { fieldName: 'entityLabel', fieldType: 'simple' },
        { fieldName: 'parentEntityClass', fieldType: 'simple' },
        { fieldName: 'parentEntityId', fieldType: 'simple' },
        { fieldName: 'parentEntityCode', fieldType: 'simple' },
        { fieldName: 'rootEntityClass', fieldType: 'simple' },
        { fieldName: 'rootEntityId', fieldType: 'simple' },
        { fieldName: 'rootEntityCode', fieldType: 'simple' },
        { fieldName: 'sourceEntityClass', fieldType: 'simple' },
        { fieldName: 'sourceEntityId', fieldType: 'simple' },
        { fieldName: 'sourceEntityCode', fieldType: 'simple' },
        { fieldName: 'routeName', fieldType: 'simple' },
        { fieldName: 'requestMethod', fieldType: 'simple' },
        { fieldName: 'requestPath', fieldType: 'simple' },
        { fieldName: 'commandName', fieldType: 'simple' },
        { fieldName: 'connectionName', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' }
    ]
};

export default initialState;
